import {
    Box,
    CircularProgress,
    Grid,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Tooltip,
} from '@mui/material';
import LineChart from 'common/components/charts/line-chart';
import React, { useEffect } from 'react';
import BarChart from 'common/components/charts/bar-chart';
import PieDoughnutChart from 'common/components/charts/pie-doughnut-chart';
import CampaignStatus from 'pages/admin/campaign/components/campaign-status';
import { ICardInformation, ICampaignOverview } from 'types/campaign';

import CampaignExportButton from 'pages/admin/campaign/components/campaign-export-button';
import { campaignOverview } from 'services/campaign.service';
import { campaignsOverviewResponseFormatter } from 'utils/campaign_helpers';

import { useTranslation } from 'react-i18next';
import { createStyleSheet, formatCurrency, handleError } from 'utils/helpers';
import { useParams } from 'react-router-dom';
import { common } from '@mui/material/colors';
import { colors } from 'common/constants';
import { ReactComponent as ID } from 'assets/images/icons/campaign-overview/id.svg';
import { ReactComponent as NameIcon } from 'assets/images/icons/campaign-overview/campaign-name.svg';
import { ReactComponent as DescriptionIcon } from 'assets/images/icons/campaign-overview/campaign-description.svg';
import { ReactComponent as TimeIcon } from 'assets/images/icons/campaign-overview/campaign-time.svg';
import { ReactComponent as TypeIcon } from 'assets/images/icons/campaign-overview/campaign-type.svg';
import { ReactComponent as TotalDataCollected } from 'assets/images/icons/campaign-overview/total-data-collected.svg';
import { ReactComponent as TotalParticipants } from 'assets/images/icons/campaign-overview/total-participants.svg';
import { ReactComponent as TotalDataCollectionFree } from 'assets/images/icons/campaign-overview/total-data-collection-free.svg';
import { ReactComponent as TotalPaidDataCollection } from 'assets/images/icons/campaign-overview/total-paid-data-collection.svg';
import { ReactComponent as EditCampaign } from 'assets/images/icons/question-design/pencil-question.svg';
import { CampaignContainer } from '../campaign-container';
import CampaignForm from 'pages/admin/campaign/components/campaign-form';
import { useAppDispatch, useAppSelector } from 'common/hook';
import { savePDF } from '@progress/kendo-react-pdf';
import { RootState } from 'plugins/store';
import { setIsLoading } from 'slices/loadingSlice';
import numeral from 'plugins/numeral';
import { getWinningUsers, IWinningUsers } from 'services/reward.service';
import {
    selectSurveyOverviewCardColors,
    selectChartDataCollectionColors,
    selectChartDeviceColors,
    selectChartRegionColors,
} from 'common/theme-selectors';

const CampaignOverview: React.FC = () => {
    const { t } = useTranslation();
    const currentCampaign = useAppSelector(
        (state) => state.campaignState.campaign
    );
    const [openModalCampaign, setOpenModalCampaign] =
        React.useState<boolean>(false);
    const [currentOverview, setCurrentOverview] = React.useState<
        ICampaignOverview | undefined
    >(undefined);

    const campaignId = useParams<{ id: string }>().id;
    const campaignOverviewSearchForm = useAppSelector(
        (state: RootState) => state.campaignState.campaignOverviewSearchForm
    );
    const dispatch = useAppDispatch();
    const [winningUsers, setWinningUsers] = React.useState<IWinningUsers[]>([]);
    const [loadingWinningUsers, setLoadingWinningUsers] = React.useState(false);
    const surveyOverviewColors = useAppSelector(selectSurveyOverviewCardColors);
    const chartDataCollectionColors = useAppSelector(
        selectChartDataCollectionColors
    );
    const chartDeviceColors = useAppSelector(selectChartDeviceColors);
    const chartRegionColors = useAppSelector(selectChartRegionColors);

    // Cột thiết bị: ưu tiên màu config (chartDeviceColors), fallback đã nằm trong selector.
    const chartDeviceSeriesData = (
        (currentOverview?.chartDevice?.data as
            | (number | { value?: number })[]
            | undefined) || []
    ).map((item, i) => ({
        value: typeof item === 'number' ? item : item?.value,
        itemStyle: { color: chartDeviceColors[i] },
    }));

    useEffect(() => {
        const fetchWinningUsers = async () => {
            if (currentCampaign?.kind !== 'page_logic' || !campaignId) return;

            setLoadingWinningUsers(true);
            try {
                const response = await getWinningUsers(Number(campaignId));
                if (response.success && response.data) {
                    setWinningUsers(response.data.winning_users || []);
                }
            } catch (error) {
                handleError(error);
            } finally {
                setLoadingWinningUsers(false);
            }
        };

        fetchWinningUsers();
    }, [currentCampaign?.kind, campaignId]);

    useEffect(() => {
        const fetchDataOverview = async () => {
            try {
                dispatch(setIsLoading(true));
                const params = {
                    ...campaignOverviewSearchForm,
                };
                if (params.service?.includes('all')) {
                    params.service = [];
                }
                if (params.device?.includes('all')) {
                    params.device = [];
                }
                if (params.area?.includes('all')) {
                    params.area = [];
                }
                const response = await campaignOverview(
                    Number(campaignId),
                    params
                );
                dispatch(setIsLoading(false));
                setCurrentOverview(
                    campaignsOverviewResponseFormatter(response.data)
                );
            } catch (error) {
                dispatch(setIsLoading(false));
                handleError(error);
            }
        };
        if (currentCampaign && currentCampaign.id) {
            fetchDataOverview();
        }
    }, [t, currentCampaign, campaignId, campaignOverviewSearchForm, dispatch]);

    const container = React.useRef(null);
    const exportPDFWithMethod = () => {
        const element = container.current || document.body;
        savePDF(element, {
            paperSize: 'auto',
            margin: 40,
            fileName: `Tổng quan chiến dịch ${currentCampaign?.campaign}`,
        });
    };
    return (
        <CampaignContainer>
            <Grid container spacing={4}>
                <Grid item xs={12} md={3}>
                    <Box
                        sx={{
                            borderRadius: '15px',
                            height: '100%',
                            overflow: 'auto',
                            backgroundColor: common.white,
                        }}
                    >
                        <Typography variant="h6" sx={styles.campaignTitle}>
                            Thông tin chiến dịch
                            {currentCampaign?.editable && (
                                <EditCampaign
                                    style={{
                                        cursor: 'pointer',
                                        width: '14px',
                                        height: '14px',
                                        marginLeft: '10px',
                                    }}
                                    onClick={() => setOpenModalCampaign(true)}
                                />
                            )}
                        </Typography>
                        <Box
                            display={'grid'}
                            gridTemplateColumns={{
                                md: 'repeat(1, 1fr)',
                            }}
                        >
                            <Stack sx={styles.row}>
                                <ID />
                                <Stack
                                    direction={'row'}
                                    justifyContent={'space-between'}
                                    alignItems={'center'}
                                    flex={1}
                                >
                                    <Box
                                        sx={{
                                            fontWeight: '600',
                                            fontSize: '16px',
                                        }}
                                    >
                                        ID
                                    </Box>
                                    <Stack
                                        flexDirection={'row'}
                                        alignItems={'center'}
                                        gap={1}
                                    >
                                        <CampaignStatus
                                            status={
                                                currentCampaign?.status_client as string
                                            }
                                        />
                                    </Stack>
                                </Stack>
                            </Stack>
                            <Stack>
                                <Box
                                    sx={{
                                        padding: '17px 35px',
                                        width: '100%',
                                        fontSize: '12px',
                                    }}
                                >
                                    ID-{currentCampaign?.id}
                                </Box>
                            </Stack>
                            <Stack sx={styles.row}>
                                <NameIcon />
                                <Box
                                    sx={{
                                        fontWeight: '600',
                                        fontSize: '16px',
                                    }}
                                >
                                    Tên chiến dịch
                                </Box>
                            </Stack>
                            <Stack>
                                <Box
                                    sx={{
                                        padding: '17px 35px',
                                        width: '100%',
                                        fontSize: '12px',
                                    }}
                                >
                                    {currentCampaign?.campaign}
                                </Box>
                            </Stack>
                            <Stack sx={styles.row}>
                                <DescriptionIcon />
                                <Box
                                    sx={{
                                        fontWeight: '600',
                                        fontSize: '16px',
                                    }}
                                >
                                    Mô tả
                                </Box>
                            </Stack>
                            <Stack>
                                <Box
                                    sx={{
                                        padding: '17px 35px',
                                        width: '100%',
                                        fontSize: '12px',
                                    }}
                                >
                                    {currentCampaign?.description}
                                </Box>
                            </Stack>
                            <Stack sx={styles.row}>
                                <TimeIcon />
                                <Box
                                    sx={{
                                        fontWeight: '600',
                                        fontSize: '16px',
                                    }}
                                >
                                    Thời gian
                                </Box>
                            </Stack>
                            <Stack>
                                <Box
                                    sx={{
                                        padding: '17px 35px',
                                        width: '100%',
                                        fontSize: '12px',
                                    }}
                                >
                                    {currentCampaign?.timeToCollect}
                                </Box>
                            </Stack>
                            <Stack sx={styles.row}>
                                <TypeIcon />
                                <Box
                                    sx={{
                                        fontWeight: '600',
                                        fontSize: '16px',
                                    }}
                                >
                                    Loại thu thập dữ liệu
                                </Box>
                            </Stack>
                            <Stack>
                                <Box
                                    sx={{
                                        padding: '17px 35px',
                                        width: '100%',
                                        fontSize: '12px',
                                    }}
                                >
                                    {currentCampaign?.campaignKind}
                                </Box>
                            </Stack>
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12} md={9}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                            marginBottom: '40px',
                        }}
                    >
                        <Typography variant="h6" sx={styles.textOverview}>
                            Tổng quan
                        </Typography>
                        <CampaignExportButton
                            onClick={exportPDFWithMethod}
                            sx={{
                                width: '177px',
                            }}
                        />
                    </Box>
                    <Box ref={container}>
                        <Grid container gap={'18px'} flexWrap={'nowrap'}>
                            {(currentOverview?.cardInformation || []).map(
                                (c: ICardInformation, index: number) => (
                                    <Grid
                                        item
                                        xs={12}
                                        md={3}
                                        lg={6}
                                        xl={3}
                                        key={index}
                                    >
                                        <Box
                                            sx={{
                                                height: '275px',
                                                width: '100%',
                                                backgroundColor: [
                                                    surveyOverviewColors
                                                        .totalCollectedData.bg,
                                                    surveyOverviewColors
                                                        .totalParticipants.bg,
                                                    surveyOverviewColors
                                                        .totalFreeCollectedData
                                                        .bg,
                                                    surveyOverviewColors
                                                        .totalPaidCollectedData
                                                        .bg,
                                                ][index],
                                                color: [
                                                    surveyOverviewColors
                                                        .totalCollectedData
                                                        .text,
                                                    surveyOverviewColors
                                                        .totalParticipants.text,
                                                    surveyOverviewColors
                                                        .totalFreeCollectedData
                                                        .text,
                                                    surveyOverviewColors
                                                        .totalPaidCollectedData
                                                        .text,
                                                ][index],
                                                borderRadius: '30px',
                                                padding: '30px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                        >
                                            <Box
                                                key={index}
                                                sx={{
                                                    marginBottom: '50px',
                                                }}
                                            >
                                                {
                                                    [
                                                        <TotalDataCollected
                                                            key={index}
                                                        />,
                                                        <TotalParticipants
                                                            key={index}
                                                        />,
                                                        <TotalDataCollectionFree
                                                            key={index}
                                                        />,
                                                        <TotalPaidDataCollection
                                                            key={index}
                                                        />,
                                                    ][index]
                                                }
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                fontWeight={700}
                                                sx={{
                                                    fontSize: '25px',
                                                    marginBottom: '10px',
                                                    lineHeight: '30px',
                                                }}
                                            >
                                                {formatCurrency(c.number)}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                fontWeight={700}
                                                sx={{
                                                    fontSize: '20px',
                                                    marginBottom: '12px',
                                                    lineHeight: '24px',
                                                }}
                                            >
                                                {c.text}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontSize: '15px',
                                                    color: [
                                                        surveyOverviewColors
                                                            .totalCollectedData
                                                            .accentText,
                                                        surveyOverviewColors
                                                            .totalParticipants
                                                            .accentText,
                                                        surveyOverviewColors
                                                            .totalFreeCollectedData
                                                            .accentText,
                                                        surveyOverviewColors
                                                            .totalPaidCollectedData
                                                            .accentText,
                                                    ][index],
                                                    lineHeight: '18px',
                                                }}
                                            >
                                                {c.recent_change}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )
                            )}
                        </Grid>
                        <Grid container marginTop={4}>
                            <Grid item xl={12} md={12}>
                                <Box
                                    sx={{
                                        backgroundColor: common.white,
                                        padding: 4,
                                        minHeight: '468px',
                                        width: '100%',
                                        borderRadius: '30px',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={styles.boxTitle}
                                    >
                                        Dữ liệu thu thập
                                    </Typography>
                                    {currentOverview?.chartDataCollected && (
                                        <LineChart
                                            style={{ height: '400px' }}
                                            option={{
                                                color: chartDataCollectionColors,
                                                title: {
                                                    text: '',
                                                },
                                                tooltip: {
                                                    trigger: 'axis',
                                                    axisPointer: {
                                                        type: 'cross',
                                                    },
                                                },
                                                toolbox: {
                                                    show: false,
                                                },
                                                grid: {
                                                    left: '5%',
                                                    top: '5%',
                                                    right: '5%',
                                                    bottom: '15%',
                                                },
                                                legend: {
                                                    itemGap: 150,
                                                    icon: 'square',
                                                    top: 'bottom',
                                                    data: [
                                                        'Dữ liệu miễn phí',
                                                        'Dữ liệu dịch vụ',
                                                    ],
                                                    itemWidth: 22,
                                                    itemHeight: 22,
                                                },
                                                xAxis: {
                                                    type: 'category',
                                                    boundaryGap: false,
                                                    data: currentOverview
                                                        ?.chartDataCollected
                                                        ?.x_axis_data,
                                                },
                                                yAxis: {
                                                    type: 'value',
                                                },
                                                series: [
                                                    {
                                                        name: 'Dữ liệu miễn phí',
                                                        type: 'line',
                                                        smooth: true,
                                                        data: currentOverview
                                                            ?.chartDataCollected
                                                            ?.free_data,
                                                    },
                                                    {
                                                        name: 'Dữ liệu dịch vụ',
                                                        type: 'line',
                                                        smooth: true,
                                                        data: currentOverview
                                                            ?.chartDataCollected
                                                            ?.paid_data,
                                                    },
                                                ],
                                            }}
                                        />
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid container marginTop={1} spacing={4}>
                            <Grid
                                item
                                xl={6}
                                md={6}
                                xs={12}
                                lg={6}
                                sx={{ paddingTop: 0 }}
                            >
                                <Box
                                    sx={{
                                        minHeight: '414px',
                                        backgroundColor: common.white,
                                        padding: 4,
                                        borderRadius: '30px',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={styles.boxTitle}
                                    >
                                        Thiết bị
                                    </Typography>
                                    {currentOverview?.chartDevice && (
                                        <BarChart
                                            option={{
                                                color: chartDeviceColors,
                                                xAxis: {
                                                    type: 'category',
                                                    data: currentOverview
                                                        ?.chartDevice
                                                        ?.x_axis_data,
                                                },
                                                tooltip: {
                                                    trigger: 'axis',
                                                    axisPointer: {
                                                        type: 'shadow',
                                                    },
                                                },
                                                grid: {
                                                    left: '5%',
                                                    top: '15%',
                                                    right: '5%',
                                                    bottom: '10%',
                                                    containLabel: true,
                                                },

                                                yAxis: {
                                                    type: 'value',
                                                },
                                                series: [
                                                    {
                                                        data: chartDeviceSeriesData,
                                                        type: 'bar',
                                                        colorBy: 'data',
                                                        itemStyle: {
                                                            borderRadius: [
                                                                50, 50, 0, 0,
                                                            ],
                                                        },
                                                        barWidth: 36,
                                                    },
                                                ],
                                            }}
                                        />
                                    )}
                                </Box>
                            </Grid>
                            <Grid
                                item
                                xl={6}
                                md={6}
                                xs={12}
                                lg={6}
                                sx={{ paddingTop: 0 }}
                            >
                                <Box
                                    sx={{
                                        minHeight: '414px',
                                        backgroundColor: common.white,
                                        padding: 4,
                                        borderRadius: '30px',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={styles.boxTitle}
                                    >
                                        Khu vực
                                    </Typography>
                                    {currentOverview?.chartArea && (
                                        <PieDoughnutChart
                                            option={{
                                                color: chartRegionColors,
                                                title: '',
                                                tooltip: {
                                                    trigger: 'item',
                                                },
                                                legend: {
                                                    type: 'scroll',
                                                    orient: 'vertical',
                                                    top: 'center',
                                                    left: '55%',
                                                    icon: 'circle',
                                                    formatter: (
                                                        name: string
                                                    ) => {
                                                        const data =
                                                            currentOverview
                                                                ?.chartArea
                                                                ?.data || [];

                                                        const total =
                                                            data.reduce(
                                                                (
                                                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                                    sum: any,
                                                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                                    item: any
                                                                ) =>
                                                                    sum +
                                                                    Number(
                                                                        item.value
                                                                    ),
                                                                0
                                                            );

                                                        const item = data.find(
                                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                            (item: any) =>
                                                                item.name ===
                                                                name
                                                        );
                                                        if (!item) return name;

                                                        const value = Number(
                                                            item.value
                                                        );
                                                        const percent = total
                                                            ? (value / total) *
                                                              100
                                                            : 0;

                                                        return `{name|${name}}  {percent|${numeral(value).format('0.0a')} (${percent.toFixed(1)}%)}`;
                                                    },
                                                    textStyle: {
                                                        rich: {
                                                            name: {
                                                                width: 80,
                                                                align: 'left',
                                                            },
                                                            percent: {
                                                                width: 80,
                                                                align: 'right',
                                                                fontWeight:
                                                                    'bold',
                                                            },
                                                        },
                                                    },
                                                },

                                                series: [
                                                    {
                                                        name: 'Top khu vực',
                                                        type: 'pie',
                                                        radius: ['45%', '65%'],
                                                        center: ['25%', '50%'],
                                                        avoidLabelOverlap:
                                                            false,
                                                        label: {
                                                            color: '#000',
                                                            fontSize: '32',
                                                            position: 'center',
                                                            fontWeight: 'bold',
                                                            formatter: () => {
                                                                return `${numeral(
                                                                    currentOverview?.chartArea?.data.reduce(
                                                                        (
                                                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                                            sum: any,
                                                                            item: {
                                                                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                                                value: any;
                                                                            }
                                                                        ) =>
                                                                            sum +
                                                                            item.value,
                                                                        0
                                                                    )
                                                                ).format(
                                                                    '0.0a'
                                                                )}`;
                                                            },
                                                        },
                                                        labelLine: {
                                                            show: false,
                                                        },
                                                        data: currentOverview
                                                            ?.chartArea
                                                            ?.data || [
                                                            {
                                                                name: '',
                                                                value: '',
                                                            },
                                                        ],
                                                    },
                                                ],
                                                graphic: {
                                                    type: 'text',
                                                    left: 'center',
                                                    top: 'center',
                                                    style: {
                                                        text: '',
                                                        textAlign: 'center',
                                                        fill: '#333',
                                                        fontSize: 24,
                                                        fontWeight: 'bold',
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                </Box>
                            </Grid>
                            {currentCampaign?.kind === 'page_logic' && (
                                <Grid item xl={12} md={12}>
                                    <Box
                                        sx={{
                                            backgroundColor: common.white,
                                            padding: 4,
                                            borderRadius: '30px',
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={styles.boxTitle}
                                            mb={3}
                                        >
                                            Danh sách người trúng thưởng
                                        </Typography>
                                        {loadingWinningUsers ? (
                                            <Box
                                                display="flex"
                                                justifyContent="center"
                                                alignItems="center"
                                                minHeight={200}
                                            >
                                                <CircularProgress />
                                            </Box>
                                        ) : winningUsers.length === 0 ? (
                                            <Box
                                                display="flex"
                                                justifyContent="center"
                                                alignItems="center"
                                                minHeight={200}
                                            >
                                                <Typography
                                                    color="text.secondary"
                                                    fontSize={16}
                                                >
                                                    Chưa có người trúng thưởng
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <TableContainer
                                                component={Paper}
                                                variant="outlined"
                                                sx={{
                                                    boxShadow: 'none',
                                                    border: 'none',
                                                }}
                                            >
                                                <Table
                                                    sx={{
                                                        borderCollapse:
                                                            'collapse',
                                                    }}
                                                >
                                                    <TableHead>
                                                        {[
                                                            'ID',
                                                            'Tên',
                                                            'Email',
                                                            'Phần thưởng',
                                                            'Thời gian nhận thưởng',
                                                        ].map(
                                                            (header, index) => (
                                                                <TableCell
                                                                    sx={{
                                                                        border: 'none',
                                                                        fontWeight: 600,
                                                                        fontSize:
                                                                            '18px',
                                                                        lineHeight:
                                                                            '21.78px',
                                                                    }}
                                                                    key={index}
                                                                >
                                                                    {header}
                                                                </TableCell>
                                                            )
                                                        )}
                                                    </TableHead>
                                                    <TableBody>
                                                        {winningUsers.map(
                                                            (item, index) => (
                                                                <TableRow
                                                                    key={
                                                                        item.id
                                                                    }
                                                                    hover
                                                                >
                                                                    <TableCell>
                                                                        {index +
                                                                            1}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Tooltip
                                                                            title={
                                                                                item
                                                                                    .attributes
                                                                                    .user
                                                                                    ?.name ||
                                                                                '-'
                                                                            }
                                                                            arrow
                                                                        >
                                                                            <Typography
                                                                                sx={{
                                                                                    overflow:
                                                                                        'hidden',
                                                                                    textOverflow:
                                                                                        'ellipsis',
                                                                                    whiteSpace:
                                                                                        'nowrap',
                                                                                    maxWidth:
                                                                                        '200px',
                                                                                }}
                                                                            >
                                                                                {item
                                                                                    .attributes
                                                                                    .user
                                                                                    ?.name ||
                                                                                    '-'}
                                                                            </Typography>
                                                                        </Tooltip>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Tooltip
                                                                            title={
                                                                                item
                                                                                    .attributes
                                                                                    .user
                                                                                    ?.email ||
                                                                                '-'
                                                                            }
                                                                            arrow
                                                                        >
                                                                            <Typography
                                                                                sx={{
                                                                                    overflow:
                                                                                        'hidden',
                                                                                    textOverflow:
                                                                                        'ellipsis',
                                                                                    whiteSpace:
                                                                                        'nowrap',
                                                                                    maxWidth:
                                                                                        '200px',
                                                                                }}
                                                                            >
                                                                                {item
                                                                                    .attributes
                                                                                    .user
                                                                                    ?.email ||
                                                                                    '-'}
                                                                            </Typography>
                                                                        </Tooltip>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Stack
                                                                            direction="row"
                                                                            alignItems="center"
                                                                            spacing={
                                                                                1
                                                                            }
                                                                        >
                                                                            {item
                                                                                .attributes
                                                                                .reward
                                                                                ?.attributes
                                                                                .image && (
                                                                                <Box
                                                                                    sx={{
                                                                                        width: 40,
                                                                                        height: 40,
                                                                                        backgroundImage: `url(${item.attributes.reward.attributes.image?.url || ''})`,
                                                                                        backgroundSize:
                                                                                            'cover',
                                                                                        backgroundPosition:
                                                                                            'center',
                                                                                        borderRadius: 1,
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            <Typography>
                                                                                {item
                                                                                    .attributes
                                                                                    .reward
                                                                                    ?.attributes
                                                                                    ?.name ||
                                                                                    '-'}
                                                                            </Typography>
                                                                        </Stack>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {item
                                                                            .attributes
                                                                            .updated_at
                                                                            ? new Date(
                                                                                  item.attributes.updated_at
                                                                              ).toLocaleDateString(
                                                                                  'vi-VN'
                                                                              )
                                                                            : '-'}
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </Grid>
            </Grid>
            {openModalCampaign && (
                <CampaignForm
                    actionType={'edit'}
                    open={openModalCampaign}
                    onClose={() => setOpenModalCampaign(false)}
                />
            )}
        </CampaignContainer>
    );
};
const styles = createStyleSheet({
    dataGrid: {
        '--DataGrid-rowBorderColor': 'transparent',
        '.MuiDataGrid-columnHeaders': {
            height: '83px',
            justifyContent: 'center',
        },
        '.MuiDataGrid-columnHeader': {
            padding: 0,
        },
        '.MuiDataGrid-columnHeaderTitle': {
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 700,
        },
        '.MuiDataGrid-columnHeaderTitleContainer': {
            display: 'flex',
        },
        '.MuiDataGrid-cell': {
            outline: 'none !important',
            padding: '0',
        },
        '.MuiDataGrid-footerContainer': {
            borderColor: 'transparent',
            marginBottom: '44px',
            marginRight: '54px',
        },
        '.MuiTablePagination-displayedRows': {
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '17px',
        },
        border: 'none',
    },
    row: {
        backgroundColor: '#F7F7F6',
        flexDirection: 'row',
        alignItems: 'center',
        padding: '17px 35px',
        gap: 1,
    },
    campaignTitle: {
        padding: '48px 30px 24px 30px',
        fontWeight: '700',
        textAlign: 'center',
        fontSize: '26px',
        lineHeight: '30px',
    },
    textOverview: {
        fontSize: '28px',
        lineHeight: '33px',
        color: colors.main,
        fontWeight: '700',
    },
    boxTitle: {
        fontWeight: 'bold',
        fontSize: '22px',
    },
});

export default CampaignOverview;
